// @flow
import React, { forwardRef, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { upperFirst } from 'lodash';
import classnames from 'classnames';

import { useRouter } from 'Client/router/useRouter';
import {
  FACET_QUERY_PARAMS,
  navigateToSearchPage,
} from 'Components/widgets/facet-search/FacetSearch.utils';
import { changeSearchQuery } from 'Store/faceting/facetingSearch.action';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { Button } from 'Components/frameworks/button/Button.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { getKidsValidationState } from 'Store/faceting/faceting.selector';
import {
  getDepartureDate,
  getTripDuration,
  getDepartureCity,
} from 'Store/trip-criteria/tripCriteria.selector';
import { getKids } from 'Store/trip-information/storeHelper';

type SearchQueryProps = {
  autocompleteQuery: string,
  clearGlobalSearch: () => void,
  onChangeQuery: (e: SyntheticEvent<HTMLInputElement>) => void,
  trackingData: {
    categoryGA: string,
    actionGA: string,
  },
};

export const ReferencedSearchQuery = (props: {
  ...$Exact<SearchQueryProps>,
  searchInputRef: *,
}) => {
  const {
    autocompleteQuery,
    clearGlobalSearch,
    onChangeQuery,
    searchInputRef,
    trackingData,
  } = props;

  const { isDesktop } = useContext(ResponsiveContext);
  const departureCity = useSelector(getDepartureCity);
  const departureDate = useSelector(getDepartureDate);
  const duration = useSelector(getTripDuration);
  const kids = useSelector(getKids);
  const kidsValidationState = useSelector(getKidsValidationState);
  const dispatch = useDispatch();
  const { location } = useRouter<{}, SearchQueryParams>();

  const buttonSize = isDesktop ? 'large' : 'medium';
  const containerPadding = isDesktop ? { paddingLeft: '15%', paddingRight: '15%' } : {};

  return (
    <div className="bg-white" style={containerPadding}>
      <div
        className={classnames(
          'm-auto max-w-site w-full py-6 flex flex-no-wrap items-stretch justify-between border-0 border-gray-light',
          {
            'border-b-1': isDesktop && autocompleteQuery.length > 0,
          },
        )}
      >
        <div className="flex-grow h-12 lg:h-16">
          <label
            className="block pb-1 text-sm lg:text-md text-gray-medium transition-color"
            htmlFor="global-search-query-input"
          >
            {upperFirst(t('faceting.autocomplete.enterYourSearch'))}
          </label>
          <input
            className="reset-input text-current cursor-pointer w-full leading-normal font-bold text-xl lg:text-2xl text-gray"
            id="global-search-query-input"
            data-testid="global-search-input"
            onChange={onChangeQuery}
            placeholder={t('faceting.searchTitle')}
            ref={searchInputRef}
            type="search"
            value={autocompleteQuery}
          />
        </div>
        <div className="flex justify-end" style={{ minWidth: isDesktop ? 190 : 110 }}>
          <Button
            className="self-center flex-none ml-1 lg:ml-2 lg:justify-center lg:leading-none"
            color={COLORS.WHITE}
            colorBg={COLORS.BLUE}
            colorBorder={COLORS.BLUE}
            dataTestId="global-search-search-button"
            isExternalLink={false}
            minWidth="initial"
            onClick={() => {
              const query = { label: autocompleteQuery };
              navigateToSearchPage({
                criteriaKeysToPersist: FACET_QUERY_PARAMS,
                location,
                searchCriteria: {
                  query,
                  departureCity,
                  departureDate,
                  duration,
                  keywords: '',
                  kids,
                  kidsValidationState,
                },
                searchPageRouteName: 'search',
              });
              dispatch(changeSearchQuery(query));
              clearGlobalSearch();
            }}
            size={buttonSize}
            style={{ flexBasis: isDesktop ? 80 : 50 }}
            trackingData={{
              categoryGA: 'layer',
              actionGA: 'search',
              labelGA: 'validate',
              searchReferrer: 'layer',
            }}
            url={autocompleteQuery ? `/s?search=${autocompleteQuery}` : '/s'}
          >
            <Icon name={ICONS.SEARCH_GLASS} width="1rem" />
          </Button>
          <Button
            className="self-center flex-none ml-1 lg:ml-2 lg:justify-center lg:leading-none"
            color={COLORS.WHITE}
            colorBg={COLORS.WHITE}
            colorBorder={COLORS.GRAY_MEDIUM}
            dataTestId="global-search-close-button"
            minWidth="initial"
            onClick={clearGlobalSearch}
            size={buttonSize}
            style={{ flexBasis: isDesktop ? 80 : 50 }}
            trackingData={{
              ...trackingData,
              labelGA: 'close',
            }}
          >
            <Icon name={ICONS.CROSS} color={COLORS.GRAY_MEDIUM} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SearchQuery = forwardRef<SearchQueryProps, *>((props: *, ref: *) => (
  <ReferencedSearchQuery {...props} searchInputRef={ref} />
));
